import { ChatMessages } from "@storytell/cp-client";
import {
  BlockRenderer as BlockRendererEnum,
  determinePreferredRenderer,
  type StreamingBlock,
  type StreamingOperation,
} from "@storytell/streaming";
import type { BlockRendererKey } from "../runtime/blockRegistry";
import { blockJsonType } from "./parseBlockContent";

interface ClassifyBlockArgs {
  block: StreamingBlock;
  /** Granular streaming content the caller tracks via subscribeToBlockDeltas. */
  streamingContent: string | undefined;
  /** The block's parent operation (drives reasoning/answer detection). */
  operation: StreamingOperation | undefined;
  /** Sibling-block lookup, for the artifact-card-skip rule. */
  getBlock: (blockId: string) => StreamingBlock | undefined;
}

const operationHasBlockWith = (
  operation: StreamingOperation | undefined,
  getBlock: (blockId: string) => StreamingBlock | undefined,
  predicate: (block: StreamingBlock) => boolean
): boolean => {
  if (!operation) return false;
  return (operation.blocks ?? []).some((blockId) => {
    const block = getBlock(blockId);
    return block ? predicate(block) : false;
  });
};

/**
 * Picks the renderer key for a block. The order mirrors the original
 * `BlockRenderer` `<Switch>` exactly — first match wins, so the sequence is
 * load-bearing (e.g. retry/error take precedence over content-type detection,
 * and the artifact-card-skip rule sits just before the artifact renderer).
 */
export function classifyBlock(args: ClassifyBlockArgs): BlockRendererKey {
  const { block, streamingContent, operation, getBlock } = args;
  const preferred = determinePreferredRenderer(block);
  const jsonType = blockJsonType(block, streamingContent);

  if (block.finalContent?.type === "retry") return "retry";
  if (block.finalContent?.type === "error") return "error";
  if (jsonType === "image") return "image";
  if (jsonType === "video") return "video";
  if (jsonType === "ask-user") return "askUser";
  if (jsonType === "chart") return "chart";
  if (block.activity === "report_generation" || jsonType === "report") return "report";
  if (jsonType === "task_result") return "task";
  if (block.title === "Summary" || jsonType === "summary") return "summary";
  if (operation?.activity === "reasoning") return "reasoning";
  if (block.activity === "presentation_generation" || jsonType === "presentation") return "presentation";
  if (jsonType === "scout") return "scout";
  if (jsonType === "crafter") return "crafter";
  if (preferred === BlockRendererEnum.SKILLS || jsonType === "skills") return "skills";
  if (jsonType === "memory") return "memory";
  if (jsonType === "calendar_events") return "calendarEvents";
  if (jsonType === "calendar_list") return "calendarList";
  if (jsonType === "freebusy") return "freebusy";
  if (jsonType === "connect_google") return "connectGoogle";
  if (jsonType === "google_setup_required") return "googleSetupRequired";
  if (jsonType === "email_list") return "emailList";
  if (jsonType === "email_modify_result") return "emailModifyResult";
  if (jsonType === "email_thread") return "emailThread";
  if (jsonType === "email_draft") return "emailDraft";
  if (jsonType === "contacts") return "contacts";
  if (jsonType === "slack_setup_required") return "slackSetupRequired";
  if (jsonType === "connect_slack") return "connectSlack";
  if (jsonType === "slack_channels") return "slackChannels";
  if (jsonType === "slack_messages") return "slackMessages";
  if (jsonType === "slack_search_results") return "slackSearchResults";
  if (jsonType === "slack_users") return "slackUsers";
  if (jsonType === "notion_search_results") return "notionSearchResults";
  if (jsonType === "notion_page") return "notionPage";
  if (jsonType === "notion_database_rows") return "notionDatabaseRows";
  if (jsonType === "notion_users") return "notionUsers";
  if (jsonType === "notion_comments") return "notionComments";
  if (jsonType === "notion_setup_required") return "notionSetupRequired";
  if (jsonType === "drive_file_list") return "driveFileList";
  if (jsonType === "drive_file") return "driveFile";
  if (jsonType === "linear_issues") return "linearIssues";
  if (jsonType === "linear_issue") return "linearIssue";
  if (jsonType === "linear_comments") return "linearComments";
  if (jsonType === "linear_teams") return "linearTeams";
  if (jsonType === "linear_projects") return "linearProjects";
  if (jsonType === "linear_initiatives") return "linearInitiatives";
  if (jsonType === "linear_document") return "linearDocument";
  if (jsonType === "linear_users") return "linearUsers";
  if (jsonType === "linear_setup_required") return "linearSetupRequired";
  if (jsonType === "github_issues") return "githubIssues";
  if (jsonType === "github_issue") return "githubIssue";
  if (jsonType === "github_pull_request") return "githubPullRequest";
  if (jsonType === "github_comments") return "githubComments";
  if (jsonType === "github_repos") return "githubRepos";
  if (jsonType === "github_users") return "githubUsers";
  if (jsonType === "github_discussions") return "githubDiscussions";
  if (jsonType === "github_discussion") return "githubDiscussion";
  if (jsonType === "github_ci_status") return "githubCIStatus";
  if (jsonType === "github_setup_required") return "githubSetupRequired";
  if (jsonType === "action_list") return "actionList";
  if (jsonType === "action_detail") return "actionDetail";
  if (jsonType === "label_list") return "labelList";
  if (jsonType === "asset_list") return "assetList";
  if (jsonType === "session_document") return "sessionDocument";
  if (jsonType === "live_session_search_results") return "liveSessionSearchResults";
  if (jsonType === "live_session_load_results") return "liveSessionLoad";
  if (jsonType === "load_chat") return "loadChat";
  if (jsonType === "confirm_action") return "confirmAction";
  if (jsonType === "mcp_result") return "mcpResult";
  if (jsonType === "integration_suggestion") return "integrationSuggestion";
  if (jsonType === "user_interjection") return "userInterjection";
  if (preferred === BlockRendererEnum.CHAT_SEARCH_RESULTS) return "chatSearchResults";
  if (block.webSearchResults) return "webSearchResults";
  if (block.searchResults) return "searchResults";
  if (block.structuredResults) return "structuredResults";
  if (block.imageResults) return "imageResults";
  if (block.conceptResults) return "conceptResults";
  if (preferred === BlockRendererEnum.INTERSTITIAL) return "interstitial";

  const isAnswerBlock =
    (operation?.activity === "answer" && block.messageType === ChatMessages.MessageType.CONTENT) ||
    jsonType === "answer";

  if (
    preferred === BlockRendererEnum.USER_FACING_MESSAGE ||
    preferred === BlockRendererEnum.INTERSTITIAL_CATCH ||
    isAnswerBlock
  ) {
    return "answer";
  }

  if (preferred === BlockRendererEnum.INTERSTITIAL_DATA) return "interstitialData";
  if (preferred === BlockRendererEnum.INTERSTITIAL_USER_FACING) return "interstitialUserFacing";

  if (preferred === BlockRendererEnum.ARTIFACT_VIEWER) {
    const skip =
      operationHasBlockWith(operation, getBlock, (b) => b.messageType === ChatMessages.MessageType.CONTENT) ||
      operationHasBlockWith(operation, getBlock, (b) => b.activity === "report_generation");
    return skip ? "artifactViewerSkip" : "artifactViewer";
  }

  if (preferred === BlockRendererEnum.BLOCK_PREVIEW) return "blockPreview";

  return "unknown";
}
